import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DocumentsPage } from "./pages/DocumentsPage";
import { FoldersPage } from "./pages/FoldersPage";
import { EmailsPage } from "./pages/EmailsPage";
import { ScannerPage } from "./pages/ScannerPage";
import { WorkflowPage } from "./pages/WorkflowPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AdminPage } from "./pages/AdminPage";
import { MetaNotePage } from "./pages/MetaNotePage";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-[#52525B]">Caricamento...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="w-12 h-12 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/folders" element={
        <ProtectedRoute>
          <FoldersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/emails" element={
        <ProtectedRoute>
          <EmailsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/scanner" element={
        <ProtectedRoute>
          <ScannerPage />
        </ProtectedRoute>
      } />
      
      <Route path="/workflow" element={
        <ProtectedRoute>
          <WorkflowPage />
        </ProtectedRoute>
      } />
      
      <Route path="/audit-log" element={
        <ProtectedRoute>
          <AuditLogPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminPage />
        </ProtectedRoute>
      } />
      
      <Route path="/metanote/:documentId" element={
        <ProtectedRoute>
          <MetaNotePage />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
